import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // PayPal redirect-style 결제의 token = PayPal Order ID
  const paypalOrderId = params.get("token");
  const recordIdParam = params.get("record") || params.get("id");
  const subscriptionRecord = params.get("subscription_record");
  // 구독(월간 정기결제) 승인 후 PayPal 이 붙여주는 구독 ID
  const subscriptionId = params.get("subscription_id");
  // 구독 흐름 여부 — 구독이면 주문 capture 를 시도하지 않는다.
  const isSubscriptionFlow = Boolean(subscriptionRecord || subscriptionId);

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState(
    isSubscriptionFlow ? "loading" : paypalOrderId ? "capturing" : "loading"
  );

  useEffect(() => {
    const token = localStorage.getItem("liztv_token");
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    async function run() {
      try {
        // 0) 구독(월간 정기결제) 승인 복귀 — capture 대신 구독 동기화
        if (isSubscriptionFlow) {
          setPhase("capturing");
          if (subscriptionId) {
            await fetch(`${API_URL}/api/paypal/subscriptions/sync`, {
              method: "POST",
              headers: { "Content-Type": "application/json", ...authHeaders },
              body: JSON.stringify({ subscription_id: subscriptionId }),
            }).catch(() => {});
          }
          setLoading(false);
          setPhase("done");
          return;
        }

        // 1) PayPal redirect 로 돌아온 경우 — 먼저 capture
        if (paypalOrderId) {
          setPhase("capturing");
          const res = await fetch(`${API_URL}/api/paypal/orders/capture`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders },
            body: JSON.stringify({ order_id: paypalOrderId }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(data.detail || "결제 처리에 실패했습니다.");
          }
          if (data.record) {
            setRecord(data.record);
            setLoading(false);
            setPhase("done");
            // 정회원/멤버십 결제 완료 시 자기소개서 작성 페이지로 이동
            const pid = data.record.plan_id || "";
            if (typeof pid === "string" && pid.startsWith("annual_")) {
              navigate("/profile/intro?welcome=1", { replace: true });
            } else if (typeof pid === "string" && pid.startsWith("matching_")) {
              // 매칭 컨설팅 결제 완료 → 6단계(계약서→자기소개서) 흐름 시작을 위해 자기소개서 작성으로 이동
              navigate("/profile/intro?welcome=1&matching=1", { replace: true });
            }
            return;
          }
        }

        // 2) record 만 들어온 경우 — admin endpoint 로 조회 시도
        const targetId = recordIdParam;
        if (targetId) {
          const res = await fetch(`${API_URL}/api/admin/paypal/payments`, {
            headers: authHeaders,
          });
          if (res.ok) {
            const d = await res.json();
            const r = (d.payments || []).find((p) => p.id === targetId);
            if (r) setRecord(r);
          }
        }
      } catch (err) {
        setError(err.message || "결제 확인 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
        setPhase("done");
      }
    }
    run();
  }, [paypalOrderId, recordIdParam, subscriptionRecord, subscriptionId, isSubscriptionFlow, navigate]);

  // === Capturing 상태 ===
  if (phase === "capturing" && loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-10 text-center" data-testid="payment-capturing">
          <Loader2 className="w-12 h-12 animate-spin text-burgundy-500 mx-auto mb-5" />
          <h1 className="text-xl font-bold text-stone-900 mb-2">결제를 확인하고 있습니다…</h1>
          <p className="text-stone-500 text-sm">잠시만 기다려주세요. 페이지를 닫지 마세요.</p>
        </div>
      </div>
    );
  }

  // === 에러 상태 ===
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-10 text-center" data-testid="payment-error">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-5">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-stone-900 mb-2">결제 확인 실패</h1>
          <p className="text-stone-500 text-sm mb-6">{error}</p>
          <Link to="/pricing">
            <Button className="w-full bg-burgundy-500 hover:bg-burgundy-600 text-white" data-testid="error-back-pricing">
              결제 페이지로 돌아가기
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-10 text-center" data-testid="payment-success">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-5">
          <CheckCircle2 className="w-10 h-10 text-emerald-500" />
        </div>
        <h1 className="text-2xl font-bold text-stone-900 mb-2">결제가 완료되었습니다</h1>
        <p className="text-stone-500 text-sm mb-6">
          PayPal 결제가 정상적으로 처리되었습니다.<br />
          영수증은 PayPal 등록 이메일로 발송됩니다.
        </p>

        {record ? (
          <div className="bg-stone-50 rounded-xl p-4 text-left text-sm space-y-1 mb-6 border border-stone-100">
            <div className="flex justify-between"><span className="text-stone-500">상품</span><span className="font-medium">{record.label}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">결제 금액</span><span className="font-semibold text-stone-900">USD ${record.amount_usd?.toLocaleString()}</span></div>
            {record.captured_at && (
              <div className="flex justify-between"><span className="text-stone-500">결제일시</span><span>{new Date(record.captured_at).toLocaleString("ko-KR")}</span></div>
            )}
            <div className="flex justify-between"><span className="text-stone-500">상태</span><span className="text-emerald-600 font-semibold">완료</span></div>
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <Link to="/community">
            <Button className="w-full bg-burgundy-500 hover:bg-burgundy-600 text-white" data-testid="success-back-home">
              커뮤니티로 이동
            </Button>
          </Link>
          <Link to="/my-corner">
            <Button variant="outline" className="w-full" data-testid="success-my-corner">
              내 멤버십 상태 보기
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
